import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import EventsAPI from 'dashboard/api/planning/events';
import PricingPlansAPI from 'dashboard/api/planning/pricingPlans';
import { pricingLineKey } from './pricingLineKey';

/**
 * Current-first event pricing with optional compare against an issued quote.
 * When quotes are off, supports locking a price snapshot in event metadata.
 */
export function useEventPricing(eventId, options = {}) {
  const store = useStore();
  const selectedPlanId = ref(null);
  const breakdown = ref(null);
  const lockedPricing = ref(null);
  const loadingPrice = ref(false);
  const locking = ref(false);
  const extraPlans = ref([]);

  const eid = () =>
    typeof eventId === 'function' ? eventId() : eventId.value || eventId;

  const quotesEnabled = computed(() => {
    const value = options.quotesEnabled;
    return !!(value?.value ?? value);
  });

  const storePlans = computed(
    () => store.getters['pricingPlans/getRecords'] || []
  );
  const plans = computed(() => {
    const storeIds = new Set(storePlans.value.map(p => p.id));
    const missing = extraPlans.value.filter(p => !storeIds.has(p.id));
    return [...storePlans.value, ...missing];
  });

  const selectedPlan = computed(() =>
    plans.value.find(p => p.id === selectedPlanId.value)
  );

  const isLocked = computed(() => !!lockedPricing.value);

  const lockedBreakdown = computed(
    () => lockedPricing.value?.breakdown || null
  );

  const breakdownTotal = bd =>
    bd?.total_excl_tax ?? bd?.total_incl_tax ?? null;

  const lockedTotal = computed(() => breakdownTotal(lockedBreakdown.value));

  // Staff surfaces always display excl-VAT totals.
  const liveTotal = computed(() => breakdownTotal(breakdown.value));

  const priceDelta = computed(() => {
    if (
      !isLocked.value ||
      lockedTotal.value === null ||
      liveTotal.value === null
    ) {
      return null;
    }
    return liveTotal.value - lockedTotal.value;
  });

  const displayTotal = computed(() =>
    isLocked.value ? lockedTotal.value : liveTotal.value
  );

  const displayBreakdown = computed(() =>
    isLocked.value ? lockedBreakdown.value : breakdown.value
  );

  const currentPlanId = () =>
    isLocked.value
      ? lockedPricing.value?.pricing_plan_id || selectedPlanId.value
      : selectedPlanId.value;

  const fetchBreakdown = async () => {
    const requestEventId = eid();
    const requestPlanId = currentPlanId();
    if (!requestEventId || !requestPlanId) {
      breakdown.value = null;
      return;
    }

    loadingPrice.value = true;
    if (!isLocked.value) breakdown.value = null;
    try {
      const response = await EventsAPI.previewPrice(
        requestEventId,
        requestPlanId
      );
      // Drop stale responses if the event/plan changed mid-flight.
      if (requestEventId !== eid() || requestPlanId !== currentPlanId()) {
        return;
      }
      breakdown.value = response.data;
    } catch {
      if (requestEventId === eid() && requestPlanId === currentPlanId()) {
        breakdown.value = null;
      }
    } finally {
      if (requestEventId === eid() && requestPlanId === currentPlanId()) {
        loadingPrice.value = false;
      }
    }
  };

  const loadPlans = async () => {
    if (!plans.value.length) {
      await store.dispatch('pricingPlans/get');
    }
  };

  const loadFromEvent = async eventData => {
    const locked = eventData?.metadata?.locked_pricing || null;
    lockedPricing.value = locked;
    selectedPlanId.value =
      locked?.pricing_plan_id || eventData?.pricing_plan_id || null;
    if (!selectedPlanId.value) breakdown.value = null;

    if (
      selectedPlanId.value &&
      !plans.value.find(p => p.id === selectedPlanId.value)
    ) {
      try {
        const res = await PricingPlansAPI.get(selectedPlanId.value);
        if (res.data) extraPlans.value = [res.data];
      } catch {
        // plan not accessible
      }
    }
  };

  const reset = () => {
    selectedPlanId.value = null;
    breakdown.value = null;
    lockedPricing.value = null;
    loadingPrice.value = false;
    locking.value = false;
    extraPlans.value = [];
  };

  const switchPlan = async planId => {
    if (isLocked.value) return;
    const id = eid();
    selectedPlanId.value = planId;
    if (id) {
      await store.dispatch('planningEvents/update', {
        id,
        pricing_plan_id: planId,
      });
    }
    await fetchBreakdown();
  };

  const lockPrice = async () => {
    const id = eid();
    if (!id || quotesEnabled.value) return;

    locking.value = true;
    try {
      const response = await EventsAPI.lockPrice(id, selectedPlanId.value);
      lockedPricing.value = response.data;
      await store.dispatch('planningEvents/show', id);
    } finally {
      locking.value = false;
    }
  };

  const unlockPrice = async () => {
    const id = eid();
    if (!id) return;

    locking.value = true;
    try {
      await EventsAPI.unlockPrice(id);
      lockedPricing.value = null;
      await store.dispatch('planningEvents/show', id);
      await fetchBreakdown();
    } finally {
      locking.value = false;
    }
  };

  const lineKey = pricingLineKey;

  return {
    selectedPlanId,
    selectedPlan,
    plans,
    breakdown,
    lockedPricing,
    lockedBreakdown,
    isLocked,
    liveTotal,
    displayTotal,
    displayBreakdown,
    priceDelta,
    loadingPrice,
    locking,
    loadPlans,
    loadFromEvent,
    fetchBreakdown,
    switchPlan,
    lockPrice,
    unlockPrice,
    lineKey,
    reset,
    loadLockedState: loadFromEvent,
  };
}
